import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, TextField, Typography } from "@mui/material";
import { HelpOutlineRounded, SendRounded } from "@mui/icons-material";
import color from "../../../Constants/colors";
import { DealMessage } from "../../../Models/ambassador";
import { formatDateTime } from "./format";

interface Props {
  messages: DealMessage[];
  /** Whose messages are shown on the right, as one's own. */
  viewer: "ambassador" | "como";
  emptyText: string;
  /** Without it the conversation is read-only. */
  onSend?: (message: string) => Promise<unknown>;
  sendLabel?: string;
  /** A second way to send the text, e.g. como asking it as a question. */
  secondaryAction?: {
    label: string;
    icon?: React.ReactNode;
    onSend: (message: string) => Promise<unknown>;
  };
  placeholder?: string;
  hint?: string;
  pending?: boolean;
}

/** The messages between como and the ambassador of one deal. */
const DealConversation: React.FC<Props> = ({
  messages,
  viewer,
  emptyText,
  onSend,
  sendLabel,
  secondaryAction,
  placeholder,
  hint,
  pending,
}) => {
  const { t, i18n } = useTranslation("ambassador");
  const [draft, setDraft] = useState("");

  const send = async (handler = onSend) => {
    if (!handler || !draft.trim()) return;
    try {
      await handler(draft.trim());
      setDraft("");
    } catch {
      // The request already showed what went wrong; the text stays.
    }
  };

  return (
    <Box>
      {messages.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: color.gray500, mb: 2 }}>
          {emptyText}
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
          {messages.map((message) => {
            const own = message.from_ambassador === (viewer === "ambassador");
            return (
              <Box
                key={message.id}
                sx={{
                  alignSelf: own ? "flex-end" : "flex-start",
                  maxWidth: { xs: "100%", md: "80%" },
                  p: 1.5,
                  borderRadius: "12px",
                  backgroundColor: own ? color.gray100 : color.white,
                  border: `1px solid ${message.is_question ? color.orange200 : color.gray200}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    color: color.gray500,
                    mb: 0.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {message.is_question && (
                    <HelpOutlineRounded
                      sx={{ fontSize: 15, color: color.orange600 }}
                    />
                  )}
                  {message.from_ambassador
                    ? t("conversation.fromAmbassador", {
                        name: message.author_name ?? "",
                      })
                    : t("conversation.fromComo", {
                        name: message.author_name ?? "",
                      })}
                  {" · "}
                  {formatDateTime(message.created_at, i18n.language)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: color.gray900,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {message.message}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {onSend && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <TextField
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={placeholder}
            multiline
            minRows={3}
            fullWidth
            inputProps={{ maxLength: 5000, "aria-label": placeholder }}
            helperText={hint}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {secondaryAction && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={secondaryAction.icon}
                disabled={pending || !draft.trim()}
                onClick={() => send(secondaryAction.onSend)}
              >
                {secondaryAction.label}
              </Button>
            )}
            <Button
              variant="contained"
              endIcon={<SendRounded />}
              disabled={pending || !draft.trim()}
              onClick={() => send()}
            >
              {sendLabel}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DealConversation;
